import { useTranslation } from 'react-i18next'
import type { Persona } from '../types'

interface PersonaCardProps {
  persona: Persona
  onEdit: (persona: Persona) => void
  onDelete: (persona: Persona) => void
  onSetDefault: (id: string) => void
  isSettingDefault: boolean
}

export function PersonaCard({ persona, onEdit, onDelete, onSetDefault, isSettingDefault }: PersonaCardProps) {
  const { t } = useTranslation()

  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-surface-200 bg-white p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-mindeia-primary-light text-mindeia-primary font-bold text-lg">
          {persona.name[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-neutral-900 truncate">{persona.name}</h3>
            {persona.is_default && (
              <span className="rounded-full bg-mindeia-primary px-2.5 py-0.5 text-xs font-medium text-white flex-shrink-0">
                {t('personas.is_default')}
              </span>
            )}
          </div>
          {persona.description && (
            <p className="mt-0.5 text-sm text-neutral-600 line-clamp-2">{persona.description}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="rounded-full bg-mindeia-primary-light text-mindeia-primary px-2.5 py-0.5 text-xs font-medium">
          {t(`teaching.tones.${persona.tone}`)}
        </span>
        <span className="rounded-full bg-surface-100 text-neutral-700 px-2.5 py-0.5 text-xs font-medium">
          {t(`teaching.styles.${persona.pedagogy_style}`)}
        </span>
      </div>

      <div className="flex gap-2 border-t border-surface-100 pt-3">
        <button
          type="button"
          onClick={() => onEdit(persona)}
          className="min-h-[44px] flex-1 rounded-lg border border-surface-200 px-3 text-sm font-medium text-neutral-700 transition-colors hover:bg-surface-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
        >
          {t('personas.edit_action')}
        </button>
        {!persona.is_default && (
          <button
            type="button"
            onClick={() => onSetDefault(persona.id)}
            disabled={isSettingDefault}
            className="min-h-[44px] flex-1 rounded-lg border border-mindeia-primary px-3 text-sm font-medium text-mindeia-primary transition-colors hover:bg-mindeia-primary-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary disabled:opacity-50"
          >
            {t('personas.set_default')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(persona)}
          aria-label={t('personas.delete_action', { name: persona.name })}
          className="min-h-[44px] w-11 flex items-center justify-center rounded-lg border border-surface-200 text-neutral-600 transition-colors hover:border-mindeia-error hover:text-mindeia-error focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-error"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 9a1 1 0 001 1h6a1 1 0 001-1l1-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </article>
  )
}
